/**
 * ExportDeckModal - Export a deck to various formats
 */
import type { ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';

import {
  FaDesktop,
  FaDownload,
  FaExclamationTriangle,
  FaFileAlt,
  FaFileCode,
  FaGamepad,
  FaList,
  FaMarkdown,
  FaRobot,
} from 'react-icons/fa';

import Modal from '@/components/Modal/Modal';
import ExportFormatButtons from '@/features/import-export/components/ExportFormatButtons';
import ExportOptionsCheckboxes, { type ExportOptionConfig } from '@/features/import-export/components/ExportOptionsCheckboxes';
import ExportPreview from '@/features/import-export/components/ExportPreview';
import {
  DECK_EXPORT_FORMATS,
  exportDeck,
  getDeckExportDefaults,
  getDeckExportFormat,
  type DeckExportFormat,
  type DeckExportOptionsState,
} from '@/features/import-export/exporters/deckRegistry';
import type { FormatOption } from '@/features/import-export/types';
import useUserSettings from '@/features/settings/hooks/api';
import type { Deck, EnrichedDeckCard } from '@/types/deck';

// ============================================================================
// Types
// ============================================================================

export interface ExportDeckModalProps {
  isOpen: boolean;
  onClose: () => void;
  cards: EnrichedDeckCard[];
  deck: Deck;
  /** @deprecated Use `deck` prop instead */
  deckName?: string;
}

// ============================================================================
// Static data
// ============================================================================

const FORMAT_ICONS: Record<DeckExportFormat, ReactElement> = {
  mtgcraft: <FaDownload className="h-4 w-4" />,
  mtga: <FaFileAlt className="h-4 w-4" />,
  moxfield: <FaFileCode className="h-4 w-4" />,
  mtgo: <FaDesktop className="h-4 w-4" />,
  arena: <FaGamepad className="h-4 w-4" />,
  'text-summary': <FaList className="h-4 w-4" />,
  'markdown-summary': <FaMarkdown className="h-4 w-4" />,
  ai: <FaRobot className="h-4 w-4" />,
};

const FORMAT_OPTIONS: FormatOption<DeckExportFormat>[] = DECK_EXPORT_FORMATS.map((meta) => ({
  value: meta.value,
  label: meta.label,
  description: meta.description,
  icon: FORMAT_ICONS[meta.value],
}));

const DEFAULT_FORMAT: DeckExportFormat = 'mtga';

const OPTION_CONFIG: ExportOptionConfig<keyof DeckExportOptionsState>[] = [
  { key: 'includeSetInfo', label: 'Include set & collector number' },
  { key: 'verbose', label: 'Verbose card details' },
  { key: 'includeOracleText', label: 'Include oracle text' },
  { key: 'includeMaybeboard', label: 'Include maybeboard' },
];

// ============================================================================
// Component
// ============================================================================

export default function ExportDeckModal({ isOpen, onClose, cards, deck, deckName }: ExportDeckModalProps): ReactElement {
  const [format, setFormat] = useState<DeckExportFormat>(DEFAULT_FORMAT);
  const [options, setOptions] = useState<DeckExportOptionsState>(() => getDeckExportDefaults(DEFAULT_FORMAT));

  const { settings } = useUserSettings();
  const currency = settings.currency;

  const resolvedDeckName = deckName ?? deck.name;
  const formatMeta = getDeckExportFormat(format);

  // Reset options to the newly selected format's defaults so per-format
  // preferences (e.g. maybeboard on for AI Chat) don't leak across formats.
  const handleFormatChange = useCallback((next: DeckExportFormat) => {
    setFormat(next);
    setOptions(getDeckExportDefaults(next));
  }, []);

  const handleOptionsChange = useCallback((patch: Partial<DeckExportOptionsState>) => {
    setOptions((prev) => ({ ...prev, ...patch }));
  }, []);

  const exportContent = useMemo(
    () => exportDeck(format, { deck, deckName: resolvedDeckName, cards, options, currency }),
    [format, deck, resolvedDeckName, cards, options, currency],
  );

  // Copy to clipboard
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(exportContent);
      return true;
    } catch (err) {
      console.error('Failed to copy:', err);
      return false;
    }
  }, [exportContent]);

  // Download as file
  const handleDownload = useCallback(() => {
    const blob = new Blob([exportContent], { type: formatMeta.mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;

    // Generate filename: [Deck name] - [Format] - [timestamp].[ext]
    const timestamp = new Date().toISOString().split('T')[0];
    const safeName = resolvedDeckName.replace(/[^a-zA-Z0-9 ]/g, '').trim();
    a.download = `${safeName} - ${formatMeta.fileLabel} - ${timestamp}.${formatMeta.extension}`;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [exportContent, resolvedDeckName, formatMeta]);

  // Rough LLM budgeting heuristic: ~4 characters per token
  const approxTokens = Math.ceil(exportContent.length / 4);
  const previewLabel = format === 'ai' ? `Preview — ~${approxTokens.toLocaleString()} tokens` : 'Preview';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Export Deck" size="lg">
      <div className="space-y-5">
        {/* Format selector */}
        <ExportFormatButtons options={FORMAT_OPTIONS} value={format} onChange={handleFormatChange} />

        {/* Format-specific options */}
        <ExportOptionsCheckboxes
          config={OPTION_CONFIG}
          supportedOptions={formatMeta.supportedOptions}
          values={options}
          onChange={(key, value) => handleOptionsChange({ [key]: value })}
        />

        {/* Warning for format limitations */}
        {(format === 'mtga' || format === 'arena') && options.includeMaybeboard && (
          <div className="flex items-start gap-2.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-sm">
            <FaExclamationTriangle className="mt-0.5 shrink-0 text-amber-400" />
            <p className="text-amber-200/90">
              MTG Arena does not support maybeboard. These cards will be exported under the Sideboard section.
            </p>
          </div>
        )}

        {formatMeta.supportedOptions.includes('includeOracleText') && options.includeOracleText && (
          <p className="text-ash text-xs">Oracle text substantially increases the output size and the chat context it consumes.</p>
        )}

        {/* Preview */}
        <ExportPreview content={exportContent} onCopy={handleCopy} onDownload={handleDownload} label={previewLabel} />

        {/* Actions */}
        <div className="flex justify-end pt-2">
          <button type="button" onClick={onClose} className="text-ash hover:text-silver px-4 py-2 text-sm transition-colors">
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}
